import { get, set } from 'idb-keyval';
import * as serverPackageService from './services/serverPackageService';
import { isLoggedIn } from './services/apiService';
import { isCloudSyncAllowed, getCloudSyncStatus, istOnlinePaket } from './utils/premiumSyncGuard';

/**
 * ============================================================================
 * 📦 Package Sync Helper — Server-Version
 * ============================================================================
 * 
 * Server-basiert (PostgreSQL). Kein Firebase mehr.
 * Alle Paket-Operationen gehen über den eigenen Server (PostgreSQL).
 * 
 * IndexedDB dient weiterhin als lokaler Cache für Offline-Anzeige
 * und schnelle Ladezeiten beim App-Start.
 * 
 * Gleiche exportierte Funktionsnamen wie vorher, damit die
 * aufrufenden Dateien (AddPackageWithAI.js etc.) unverändert bleiben.
 * ============================================================================
 */

// ============================================================
// Hash-Berechnung (bleibt lokal — reine Utility-Funktion)
// ============================================================

/**
 * 🔐 Hash-Berechnung über alle Pakete
 * Verwendet IDs + stabile Felder für schnellen Vergleich
 */
export const ACTIVE_WINDOW_DAYS = 5;

export function getActiveWindowCutoffMs(nowMs = Date.now()) {
  const d = new Date(nowMs);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - ACTIVE_WINDOW_DAYS * 86400000;
}

export function isPackageInActiveWindow(p, cutoffMs = getActiveWindowCutoffMs()) {
  if (!p) return false;
  const raw = p.ausgegebenAm || p.ausgegeben_am;
  if (!p.ausgegeben && !p.zurueckgeschickt && !raw) return true;
  if (!raw) return true;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return true;
  return t >= cutoffMs;
}

export async function calculatePackagesHash(pakete) {
  if (!pakete || pakete.length === 0) {
    return 'empty';
  }
  
  try {
    const cutoffMs = getActiveWindowCutoffMs();
    const activePakete = pakete.filter(p => isPackageInActiveWindow(p, cutoffMs));
    if (activePakete.length === 0) return 'empty';
    const hashInput = activePakete
      .map(p => {
        const id = p.id || 'unknown';
        const name = (p.name || p.empfaengerName || '').trim();
        const tracking = (p.trackingNumber || p.sendungsnummer || '').trim();
        const ausgegeben = p.ausgegebenAm ? '1' : '0';
        const zurueck = p.zurueckgeschickt ? '1' : '0';
        // #16: regalplatz + paketAnzahl mit in den Hash — sonst propagiert eine Teilausgabe
        // (nur paketAnzahl↓, ohne ausgegebenAm) oder ein Regalplatz-Wechsel nicht.
        // MUSS exakt identisch zu server/lib/cronJobs.js sein!
        const regal = (p.regalplatz || '').trim();
        const anzahl = String(parseInt(p.paketAnzahl, 10) || 1);
        return `${id}:${name}:${tracking}:${ausgegeben}:${zurueck}:${regal}:${anzahl}`;
      })
      .sort()
      .join('|');
    
    const encoder = new TextEncoder();
    const data = encoder.encode(hashInput);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex;
  } catch (error) {
    console.error('📦 Hash-Berechnung fehlgeschlagen:', error);
    return `fallback_${pakete.length}_${Date.now()}`;
  }
}

// ============================================================
// Paket speichern / löschen — über Server-API
// ============================================================

/**
 * 💾 Speichert einzelnes Paket auf dem Server (CREATE oder UPDATE)
 * Speichert auf dem Server (PostgreSQL)
 * 
 * WICHTIG: lastUpdated wird NUR gesetzt wenn das Paket keinen hat.
 * Bei Offline-Paketen bleibt der originale Edit-Timestamp erhalten,
 * damit der Server-Timestamp-Guard korrekt vergleichen kann.
 */
export async function savePaket(paket) {
  return (await savePaketErgebnis(paket)) === 'ok';
}

/**
 * Wie savePaket, unterscheidet aber den Konflikt: → 'ok' | 'konflikt' (409: Server-Version ist neuer) | 'fehler'.
 * Bei 'konflikt' ist die lokale Kopie veraltet und darf NICHT als synchronisiert gelten — der Aufrufer übernimmt
 * die Server-Version (flushPendingPackages). Früher blieb sie uploaded:false liegen und wurde alle 20 s erneut
 * gesendet, für immer (22.10.2026: moto g05 von Green Goat, „3 Änderungen warten", 409 in Dauerschleife).
 */
export async function savePaketErgebnis(paket) {
  // Lokal-Modus (kein Premium / abgelaufene Testphase):
  // Neue Pakete bleiben rein lokal auf dem Gerät; bestehende Online-Pakete (aus der Test-/Premium-Phase)
  // werden beim Ausgeben, Zurückschicken oder Bearbeiten weiterhin mit dem Server abgeglichen.
  if (!isCloudSyncAllowed() && !istOnlinePaket(paket)) {
    return 'gesperrt';
  }
  try {
    const paketMitTimestamp = {
      ...paket,
      lastUpdated: paket.lastUpdated || Date.now(),
    };

    // Upsert via PUT (Server macht ON CONFLICT DO UPDATE)
    await serverPackageService.updatePackage(paket.id, paketMitTimestamp);
    console.log(`📦 Paket ${paket.id} auf Server gespeichert`);

    return 'ok';
  } catch (error) {
    if (error && error.status === 409) {
      console.warn(`📦 Paket ${paket.id}: Server-Version ist bereits neuer (409) — lokale Kopie ist veraltet`);
      return 'konflikt';
    }
    console.error(`📦 Fehler beim Speichern von Paket ${paket.id}:`, error);
    return 'fehler';
  }
}

/**
 * Alias: savePaketAlias = savePaket
 * (AddPackageWithAI.js nutzt beide Namen)
 */
export const savePaketAlias = savePaket;

/**
 * 🗑️ Löscht einzelnes Paket vom Server
 */
export async function deletePaket(paketId, paket = null) {
  if (!isCloudSyncAllowed() && paket && !istOnlinePaket(paket)) {
    return false;
  }
  try {
    await serverPackageService.deletePackage(paketId, true); // hard delete
    console.log(`📦 Paket ${paketId} vom Server gelöscht`);
    return true;
  } catch (error) {
    console.error(`📦 Fehler beim Löschen von Paket ${paketId}:`, error);
    return false;
  }
}

/**
 * Alias für Kompatibilität
 */
export const deletePaketAlias = deletePaket;

/**
 * 🗑️ Gelöschte IDs speichern — Server verwaltet Tombstones intern
 */
export async function saveDeletedIds(deletedIds) {
  // Server verwaltet gelöschte Pakete intern (soft-delete Flag)
  console.log(`📦 ${deletedIds.length} gelöschte IDs — Server verwaltet intern`);
}

/**
 * 🗑️ Gelöschte IDs laden — Nicht mehr nötig, Server filtert automatisch
 */
export async function loadDeletedIds(forceRefresh = false) {
  return [];
}

/**
 * Cache invalidieren — No-Op bei Server-Betrieb
 */
export function invalidateDeletedIdsCache() {
  // Nicht nötig bei Server-Betrieb
}

/**
 * 📦 Lädt EIN EINZELNES Paket vom Server
 */
export async function loadSinglePaket(paketId) {
  try {
    const pkg = await serverPackageService.getPackage(paketId);
    if (pkg) {
      const mapped = serverPackageService.mapPackageFromServer(pkg);
      console.log(`📦 ✅ Paket ${paketId} vom Server geladen`);
      return { ...mapped, uploaded: true };
    } else {
      console.log(`📦 ⚠️ Paket ${paketId} nicht auf Server gefunden`);
      return null;
    }
  } catch (error) {
    if (error.message?.includes('404')) {
      console.log(`📦 ⚠️ Paket ${paketId} nicht auf Server gefunden`);
      return null;
    }
    console.error(`📦 Fehler beim Laden von Paket ${paketId}:`, error);
    return null;
  }
}

/**
 * 🔄 Hash lokal und auf dem Server aktualisieren
 */
export async function updatePackagesHash(pakete) {
  try {
    const newHash = await calculatePackagesHash(pakete);
    
    // Lokal sofort setzen
    await set('packagesHash', newHash);
    console.log(`📦 Lokaler Hash gesetzt: ${newHash.substring(0, 8)}...`);
    
    // Server-Hash + Sync-Zeitstempel aktualisieren:
    // Im Lokal-Modus (reason === 'expired') kennt der Server nur die bestehenden Online-Pakete → deren Hash senden.
    if (isCloudSyncAllowed()) {
      await set('lastPackageSync', Date.now());
      if (navigator.onLine && isLoggedIn()) {
        await serverPackageService.updateSyncHash(newHash);
        console.log(`📦 Server-Hash aktualisiert: ${newHash.substring(0, 8)}... (${pakete.length} Pakete)`);
      }
    } else if (navigator.onLine && isLoggedIn() && getCloudSyncStatus().reason === 'expired') {
      const onlinePakete = (pakete || []).filter(istOnlinePaket);
      const onlineHash = await calculatePackagesHash(onlinePakete);
      await serverPackageService.updateSyncHash(onlineHash);
      console.log(`📦 Server-Hash (Online-Bestand) aktualisiert: ${onlineHash.substring(0, 8)}... (${onlinePakete.length} Online-Pakete)`);
    }
    
  } catch (error) {
    console.error('📦 Fehler beim Hash-Update:', error);
  }
}

// ============================================================
// Sync — 3-Stufen über Server-API
// ============================================================

/**
 * 📥 Lädt geänderte Pakete vom Server (Delta-Sync)
 */
export async function syncPackages(lastSync = 0) {
  try {
    const since = lastSync ? new Date(lastSync).toISOString() : undefined;
    const result = await serverPackageService.loadPackages(since);
    const mapped = (result.packages || []).map(serverPackageService.mapPackageFromServer);
    console.log(`📦 ${mapped.length} geänderte Pakete vom Server geladen`);
    return mapped;
  } catch (error) {
    console.error('📦 Fehler beim Delta-Sync:', error);
    return [];
  }
}

/**
 * 📥 Lädt ALLE Pakete vom Server (Full-Sync)
 */
const FULL_SYNC_COOLDOWN = 10 * 60 * 1000; // 10 Minuten

export async function loadAllPackages(forceBypass = false, { speichern = true } = {}) {
  // speichern:false — der Aufrufer schreibt das (gegen die aktuelle Liste geschützte) Ergebnis selbst (checkAndSync, 23.10.2026)
  // Cooldown prüfen
  const now = Date.now();
  if (!forceBypass) {
    const lastFullSyncTime = await get('lastFullSyncTime') || 0;
    if (lastFullSyncTime > 0 && (now - lastFullSyncTime) < FULL_SYNC_COOLDOWN) {
      const lokalePakete = await get('pakete') || [];
      // 🔧 v1.50 FIX: Cooldown NICHT blockieren wenn lokal 0 Pakete!
      // Sonst werden Server-Pakete nie geladen nach Cache-Bereinigung/Tab-Wechsel
      if (lokalePakete.length > 0) {
        const minutenSeit = Math.round((now - lastFullSyncTime) / 60000);
        console.warn(`📦 ⏭️ Full-Sync BLOCKIERT (Cooldown: letzter vor ${minutenSeit} Min)`);
        return lokalePakete;
      } else {
        console.log('📦 Cooldown aktiv, aber lokal 0 Pakete → Cooldown übergangen!');
      }
    }
  }
  
  // Cooldown-Timestamp SOFORT setzen
  await set('lastFullSyncTime', now);
  
  try {
    const result = await serverPackageService.loadPackages();
    const allePakete = (result.packages || []).map(serverPackageService.mapPackageFromServer);
    
    console.log(`📦 ${allePakete.length} Pakete komplett vom Server geladen`);
    
    // 🛡️ Ungesyncte lokale Änderungen (z.B. Offline-Ausgaben) nicht durch Full-Sync überschreiben!
    const lokaleBestand = (await get('pakete')) || [];
    let geschuetztePakete = preservePendingLocalPackages(lokaleBestand, allePakete);

    // Direkt vor dem Schreiben noch einmal gegen den Gerätespeicher: ein in der Zwischenzeit angelegtes Paket
    // (Scan während des Abgleichs) darf nicht überschrieben werden (23.10.2026)
    const nochmal = (await get('pakete')) || [];
    if (nochmal !== lokaleBestand) geschuetztePakete = preservePendingLocalPackages(nochmal, geschuetztePakete);

    // Lokal cachen
    if (speichern) await set('pakete', geschuetztePakete);
    await set('lastPackageSync', now);
    
    return geschuetztePakete;
  } catch (error) {
    console.error('📦 Fehler beim Full-Sync vom Server:', error);
    const lokalePakete = await get('pakete') || [];
    return lokalePakete;
  }
}

// ============================================================
// Merge + Vergleich
// ============================================================

/**
 * Normalisiert lastUpdated auf Millisekunden.
 * Im Datenbestand kommen ISO-Strings UND Zahlen vor — direkte Vergleiche
 * zwischen beiden Typen wären falsch.
 */
function toMs(value) {
  if (!value) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * 🛡️ Schützt alle lokal geänderten/angelegten, aber noch nicht auf dem Server
 * bestätigten Pakete (uploaded === false) vor dem Überschreiben durch Full-Sync.
 * Betrifft SOWOHL offline neu angelegte Pakete (!serverIds.has(id)) ALS AUCH
 * offline ausgegebene / zurückgeschickte / bearbeitete Pakete, deren ID auf
 * dem Server bereits existiert (dort aber noch auf dem alten Stand steht).
 */
// Frisch angelegte/geänderte Pakete, die in der Serverantwort (noch) fehlen, ebenfalls behalten (23.10.2026):
// Die Serverliste wurde abgefragt, BEVOR der Upload des Pakets ankam; inzwischen trägt es lokal schon uploaded:true
// → ohne diese Regel fiel es beim Abgleich heraus (Leistungstest mit Zweitgerät: 1–3 von 15 Paketen weg).
// Alte Pakete, die der Server wirklich gelöscht hat (Aufräumen nach Tagen), verschwinden weiter normal.
const FRISCH_MS = 10 * 60 * 1000;
export function preservePendingLocalPackages(lokalePakete, serverPakete) {
  const serverIds = new Set((serverPakete || []).map((p) => p && p.id));
  const grenze = Date.now() - FRISCH_MS;
  const pending = (lokalePakete || []).filter((p) => p && (p.uploaded === false
    || (!p.deleted && !serverIds.has(p.id) && toMs(p.lastUpdated || p.updatedAt) > grenze)));
  if (pending.length === 0) return serverPakete;

  const pendingById = new Map(pending.map((p) => [p.id, p]));
  const result = [];
  let replacedCount = 0;

  for (const sp of serverPakete) {
    const localP = pendingById.get(sp.id);
    if (localP) {
      const localTime = toMs(localP.lastUpdated || localP.updatedAt);
      const serverTime = toMs(sp.lastUpdated || sp.updatedAt);
      if (localTime >= serverTime) {
        result.push(localP);
        replacedCount++;
      } else {
        result.push({ ...sp, uploaded: true });
      }
      pendingById.delete(sp.id);
    } else {
      result.push(sp);
    }
  }

  const addedCount = pendingById.size;
  for (const localP of pendingById.values()) {
    result.push(localP);
  }

  if (replacedCount > 0 || addedCount > 0) {
    console.warn(`📦 Full-Sync-Schutz: ${replacedCount} ungesyncte lokale Änderung(en) + ${addedCount} neue lokale Paket(e) behalten`);
  }
  return result;
}

/**
 * 🔄 Merged lokale und Server-Pakete (Server hat Vorrang)
 * Ausnahme: lokale, noch nicht hochgeladene Änderungen (uploaded === false)
 * gewinnen bei gleichem/neuerem Zeitstempel, damit Offline-Edits nicht
 * stillschweigend verloren gehen.
 */
export function mergePakete(lokalePakete, serverPakete, deletedIds = []) {
  const deletedSet = new Set(deletedIds);
  const merged = new Map();

  // Erst lokale Pakete
  for (const p of lokalePakete) {
    if (!deletedSet.has(p.id)) {
      merged.set(p.id, p);
    }
  }

  // Server-Pakete überschreiben (haben Vorrang)
  for (const p of serverPakete) {
    if (deletedSet.has(p.id)) continue;
    const existing = merged.get(p.id);
    if (!existing) {
      merged.set(p.id, { ...p, uploaded: true });
      continue;
    }
    const serverTime = toMs(p.lastUpdated);
    const localTime = toMs(existing.lastUpdated);
    // Bei Gleichstand: Server gewinnt — außer lokal liegen ungesyncte Änderungen
    const localPending = existing.uploaded === false;
    const serverWins = localPending ? serverTime > localTime : serverTime >= localTime;
    if (serverWins) {
      merged.set(p.id, { ...p, uploaded: true });
    } else if (localPending) {
      console.warn(`📦 Merge-Konflikt bei ${p.id}: lokale ungesyncte Änderung behalten`);
    }
  }

  const result = Array.from(merged.values());
  console.log(`📦 Merge: ${lokalePakete.length} lokal + ${serverPakete.length} server → ${result.length} gesamt`);
  return result;
}

/**
 * 🧹 Entfernt lokal Pakete die >5 Tage nach Ausgabe/Rückgabe abgelaufen sind
 * (Spiegelt die Server-seitige Cron-Löschung)
 */
export function getConfiguredDeleteDays() {
  try {
    const raw = localStorage.getItem('kiosk_delete_days');
    if (raw === '0') return 1 / 24; // 1 Stunde Puffer für „Rückgängig" nach Ausgabe
    const n = parseInt(raw || '5', 10);
    if (Number.isFinite(n) && n >= 1) return Math.min(1825, n);
  } catch { /* */ }
  return 5;
}

export function cleanupExpiredPackages(pakete) {
  const CLEANUP_DAYS = getConfiguredDeleteDays();
  const now = Date.now();
  
  const filtered = pakete.filter(p => {
    if (!p.ausgegebenAm) return true;
    const age = (now - new Date(p.ausgegebenAm).getTime()) / (24 * 60 * 60 * 1000);
    if (p.uploaded === false && (isCloudSyncAllowed() || istOnlinePaket(p))) return true;
    // Reine Lokal-Pakete (ohne Cloud-Sync) liegen nicht im Server-Archiv → volle CLEANUP_DAYS lokal behalten
    if (!isCloudSyncAllowed() && !istOnlinePaket(p)) return age <= CLEANUP_DAYS;
    if (CLEANUP_DAYS < ACTIVE_WINDOW_DAYS) return age <= CLEANUP_DAYS;
    return isPackageInActiveWindow(p, getActiveWindowCutoffMs(now));
  });
  
  const removed = pakete.length - filtered.length;
  if (removed > 0) {
    console.log(`🧹 Cleanup: ${removed} abgelaufene Pakete lokal entfernt`);
  }
  return filtered;
}

/**
 * 🔍 Prüft ob Sync nötig ist (Hash-Vergleich mit Server)
 */
export async function checkSyncNeeded() {
  try {
    const serverSync = await serverPackageService.getSyncHash();
    const lokaleHash = await get('packagesHash');
    
    const serverHash = serverSync?.packagesHash || null;
    const needed = serverHash !== lokaleHash;
    
    console.log(`📦 Sync-Check: lokal=${(lokaleHash || 'null').substring(0, 8)} server=${(serverHash || 'null').substring(0, 8)} → ${needed ? 'NÖTIG' : 'nicht nötig'}`);
    
    return {
      needed,
      localHash: lokaleHash,
      serverHash,
      serverCount: serverSync?.packagesCount || 0
    };
  } catch (error) {
    console.error('📦 Sync-Check fehlgeschlagen:', error);
    return { needed: true, localHash: null, serverHash: null, serverCount: 0 };
  }
}

/**
 * 🔍 Holt den Server-Hash
 */
export async function getServerHash() {
  try {
    const serverSync = await serverPackageService.getSyncHash();
    return serverSync?.packagesHash || null;
  } catch (error) {
    console.error('📦 Server-Hash laden fehlgeschlagen:', error);
    return null;
  }
}

/**
 * 📦 Lädt mehrere Pakete nach IDs vom Server
 */
export async function loadMultiplePakete(paketIds) {
  if (!paketIds || paketIds.length === 0) return [];
  
  try {
    const pakete = [];
    for (const id of paketIds) {
      try {
        const pkg = await serverPackageService.getPackage(id);
        if (pkg) {
          pakete.push(serverPackageService.mapPackageFromServer(pkg));
        }
      } catch (err) {
        console.warn(`📦 Paket ${id} nicht gefunden auf Server`);
      }
    }
    
    console.log(`📦 ${pakete.length}/${paketIds.length} Pakete vom Server geladen`);
    return pakete;
  } catch (error) {
    console.error('📦 Fehler beim Laden mehrerer Pakete:', error);
    return [];
  }
}

/**
 * 📥 Delta-Sync — Server-Version
 */
export async function deltaSyncViaChronik(callbacks) {
  const { onPaketeGeladen, onCompleted, onError } = callbacks || {};
  
  try {
    const lastSync = await get('lastPackageSync') || 0;
    const since = lastSync ? new Date(lastSync).toISOString() : undefined;
    
    const result = await serverPackageService.loadPackages(since);
    const neuePakete = (result.packages || []).map(serverPackageService.mapPackageFromServer);
    
    if (neuePakete.length > 0) {
      const lokalePakete = await get('pakete') || [];
      const merged = cleanupExpiredPackages(mergePakete(lokalePakete, neuePakete));
      
      await set('pakete', merged);
      await set('lastPackageSync', Date.now());
      
      if (onPaketeGeladen) onPaketeGeladen(merged);
      console.log(`📦 Delta-Sync: ${neuePakete.length} Updates, ${merged.length} gesamt`);
    }
    
    if (onCompleted) onCompleted({ syncType: 'delta', count: neuePakete.length });
    return neuePakete;
  } catch (error) {
    console.error('📦 Delta-Sync fehlgeschlagen:', error);
    if (onError) onError(error);
    return [];
  }
}

/**
 * 🔄 Gestufter Sync (Hauptmethode)
 * 1. Hash-Check → wenn gleich: fertig
 * 2. Delta-Sync seit letztem Zeitpunkt
 * 3. Fallback: Full-Sync
 */
export async function performStagedSync(callbacks, skipFullSync = false) {
  const { onSyncStart, onStufe1, onStufe2, onStufe3, onCompleted, onError } = callbacks || {};
  
  try {
    if (onSyncStart) onSyncStart();
    
    const localHash = await get('packagesHash') || '';
    const lastSync = await get('lastPackageSync') || 0;
    
    // Stufe 1: Hash-Check
    if (onStufe1) onStufe1();
    const syncResult = await serverPackageService.syncPackages(localHash, 
      lastSync ? new Date(lastSync).toISOString() : undefined
    );
    
    if (syncResult.syncType === 'none') {
      console.log('📦 Stufe 1: Hash gleich → kein Sync nötig');
      if (onCompleted) onCompleted({ syncType: 'none', count: 0 });
      return { syncType: 'none', pakete: await get('pakete') || [] };
    }
    
    // Stufe 2: Delta
    if (syncResult.syncType === 'delta') {
      if (onStufe2) onStufe2();
      console.log(`📦 Stufe 2: Delta-Sync → ${syncResult.packages.length} Änderungen`);
      
      const lokalePakete = await get('pakete') || [];
      let merged = mergePakete(lokalePakete, syncResult.packages);
      
      // Lokaler Cleanup: Pakete >5 Tage nach Ausgabe/Rücksendung entfernen
      merged = cleanupExpiredPackages(merged);
      
      await set('pakete', merged);
      await set('packagesHash', syncResult.hash);
      await set('lastPackageSync', Date.now());
      
      // Prüfe ob Hash nach Delta-Merge stimmt — wenn nicht, Full-Sync erzwingen
      // (passiert wenn Server-Cron Pakete gelöscht hat die lokal noch existieren)
      const localHashAfterMerge = await calculatePackagesHash(merged);
      if (localHashAfterMerge !== syncResult.hash && !skipFullSync) {
        console.log(`📦 Stufe 2→3: Hash-Mismatch nach Delta (${localHashAfterMerge.substring(0,8)} vs ${syncResult.hash.substring(0,8)}) → Full-Sync`);
        if (onStufe3) onStufe3();
        const full = await serverPackageService.loadPackages();
        let fullPakete = (full.packages || []).map(serverPackageService.mapPackageFromServer);
        fullPakete = cleanupExpiredPackages(fullPakete);

        // 🛡️ Lokal geänderte/erstellte, noch nicht hochgeladene Pakete (uploaded === false)
        // dürfen vom Full-Sync nicht überschrieben werden (inkl. Offline-Ausgaben!)
        fullPakete = preservePendingLocalPackages(merged, fullPakete);

        await set('pakete', fullPakete);
        await set('packagesHash', syncResult.hash);
        await set('lastPackageSync', Date.now());
        await set('lastFullSyncTime', Date.now());
        
        if (onCompleted) onCompleted({ syncType: 'full', count: fullPakete.length });
        return { syncType: 'full', pakete: fullPakete };
      }
      
      if (onCompleted) onCompleted({ syncType: 'delta', count: syncResult.packages.length });
      return { syncType: 'delta', pakete: merged };
    }
    
    // Stufe 3: Full-Sync
    if (!skipFullSync) {
      if (onStufe3) onStufe3();
      console.log(`📦 Stufe 3: Full-Sync → ${syncResult.packages.length} Pakete`);

      let fullPakete = cleanupExpiredPackages(syncResult.packages);

      // 🛡️ Ungesyncte lokale Pakete (inkl. Offline-Ausgaben) nicht durch Full-Sync verlieren
      const lokaleBestand = await get('pakete') || [];
      fullPakete = preservePendingLocalPackages(lokaleBestand, fullPakete);

      await set('pakete', fullPakete);
      await set('packagesHash', syncResult.hash);
      await set('lastPackageSync', Date.now());
      await set('lastFullSyncTime', Date.now());
      
      if (onCompleted) onCompleted({ syncType: 'full', count: fullPakete.length });
      return { syncType: 'full', pakete: fullPakete };
    }
    
    if (onCompleted) onCompleted({ syncType: 'skipped', count: 0 });
    return { syncType: 'skipped', pakete: await get('pakete') || [] };
    
  } catch (error) {
    console.error('📦 Staged-Sync fehlgeschlagen:', error);
    if (onError) onError(error);
    return { syncType: 'error', pakete: await get('pakete') || [] };
  }
}

/**
 * ⚡ Schneller Sync-Check (nur Hash vergleichen)
 */
export async function quickSyncCheck() {
  try {
    const localHash = await get('packagesHash') || '';
    const serverSync = await serverPackageService.getSyncHash();
    const serverHash = serverSync?.packagesHash || '';
    
    return {
      inSync: localHash === serverHash && localHash !== '',
      localHash,
      serverHash,
      serverCount: serverSync?.packagesCount || 0
    };
  } catch (error) {
    console.error('📦 Quick-Sync-Check fehlgeschlagen:', error);
    return { inSync: false, localHash: '', serverHash: '', serverCount: 0 };
  }
}
